import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, type ProductDetail, type StockProducts } from '../api';
import Pager from '../components/Pager';
import { LotLink, OriginLink, PickingPdfLink } from '../components/Links';
import { StockStatusBadge } from '../components/StatusBadge';

function fmtQty(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}
function fmtDate(d: string | null): string {
  if (!d) return '—';
  const [y, m, j] = d.split('-');
  return y && m && j ? `${j}.${m}.${y}` : d;
}
function fmtMoney(n: number): string {
  return n.toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
const SENS: Record<string, string> = { in: 'Entrée', out: 'Sortie', interne: 'Interne' };

// Odoo's display_name is prefixed with "[code] " — the code already has its own
// column, so drop it to keep the Produit column narrow.
function stripCode(name: string, code: string): string {
  const prefix = `[${code}] `;
  return code && name.startsWith(prefix) ? name.slice(prefix.length) : name;
}

const FILTERS: [string, string][] = [
  ['all', 'Tout'],
  ['in_stock', 'En stock'],
  ['rupture', 'Ruptures'],
  ['below_min', 'Sous mini'],
  ['overstock', 'Surstock'],
  ['reserved', 'Réservés'],
];

const PAGE_SIZE = 50;

// Les deux onglets du ticket #25 : d'un côté les consommables, suivis par lot et
// porteurs d'une date de péremption ; de l'autre le matériel, suivi au numéro de
// série. Le départage vient du mode de suivi Odoo du produit.
const TABS = [
  { key: 'consommables', label: 'Consommables' },
  { key: 'hardware', label: 'Hardware' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export default function Stocks() {
  const [data, setData] = useState<StockProducts | null>(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Recherche, filtre et fiche ouverte vivent dans l'URL : c'est ce qui rend
  // l'écran atteignable depuis les autres (`/stocks?produit=42`,
  // `/stocks?statut=rupture`) et ce qui garde le bouton « précédent » utile.
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const status = params.get('statut') ?? 'all';
  const openId = Number(params.get('produit')) || null;
  const rawTab = params.get('onglet');
  const tab: TabKey = TABS.some((t) => t.key === rawTab) ? (rawTab as TabKey) : 'consommables';
  const [qInput, setQInput] = useState(q);
  useEffect(() => { setQInput(q); }, [q]);

  function patch(next: Record<string, string | null>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) { if (v) p.set(k, v); else p.delete(k); }
    setParams(p, { replace: true });
  }
  const setOpenId = (id: number | null) => patch({ produit: id ? String(id) : null });

  const [detail, setDetail] = useState<ProductDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    if (openId == null) { setDetail(null); return; }
    setDetailLoading(true);
    setDetailError(null);
    api.getProduct(openId)
      .then(setDetail)
      .catch((e) => setDetailError(e.message))
      .finally(() => setDetailLoading(false));
  }, [openId]);

  // Recherche, filtre de statut ET page sont résolus par le serveur : un filtre ne
  // se limite plus aux lignes déjà chargées.
  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    api.getStockProducts({ q, status, tab, limit: PAGE_SIZE, offset })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [q, status, tab, offset]);
  useEffect(() => { reload(); }, [reload]);

  const c = data?.counts;

  // Les compteurs d'onglet décrivent la recherche et le statut EN COURS : arriver
  // ici par `/stocks?statut=rupture` depuis l'Accueil montre donc tout de suite
  // combien de ruptures se trouvent dans l'autre onglet.
  const tabCounts = c?.tabs;

  return (
    <div className="devis-page">
      <div className="ventes-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`ventes-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => {
              patch({ onglet: t.key === 'consommables' ? null : t.key, produit: null });
              setOffset(0);
            }}
          >
            {t.label}{tabCounts ? ` (${tabCounts[t.key]})` : ''}
          </button>
        ))}
      </div>

      <div className="page-toolbar">
        <button className="devis-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
      </div>

      {c && (
        <div className="kpi-grid stock-kpis">
          <div className="kpi-card kpi-danger"><div className="kpi-label">Ruptures</div><div className="kpi-value">{c.rupture}</div></div>
          <div className="kpi-card"><div className="kpi-label">Sous mini</div><div className="kpi-value">{c.below_min}</div></div>
          <div className="kpi-card"><div className="kpi-label">Surstock</div><div className="kpi-value">{c.overstock}</div></div>
          <div className="kpi-card"><div className="kpi-label">Réservés</div><div className="kpi-value">{c.reserved}</div></div>
          <div className="kpi-card"><div className="kpi-label">Produits{c.capped ? ' (max)' : ''}</div><div className="kpi-value">{c.total}</div></div>
        </div>
      )}

      <div className="ventes-toolbar">
        <div className="suivi-filters">
          {FILTERS.map(([k, lbl]) => (
            <button
              key={k}
              className={`suivi-filter ${status === k ? 'active' : ''}`}
              onClick={() => { patch({ statut: k === 'all' ? null : k, produit: null }); setOffset(0); }}
            >{lbl}</button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); patch({ q: qInput.trim() || null, produit: null }); setOffset(0); }}>
          <input
            className="stock-search"
            placeholder="Rechercher (nom ou code)…"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
          />
        </form>
      </div>

      {/* Fiche atteinte par lien direct (`?produit=`) : le produit n'est pas
          forcément dans la page affichée — la fiche s'ouvre alors au-dessus de la
          liste, sinon elle se déplie sous sa ligne. */}
      {openId != null && !(data?.items ?? []).some((p) => p.id === openId) && (
        <div className="devis-list" style={{ marginBottom: 14 }}>
          <ProductPanel
            detail={detail} detailLoading={detailLoading} detailError={detailError}
            onClose={() => setOpenId(null)}
          />
        </div>
      )}

      <div className="devis-list stock-list">
        {loading && <div className="devis-empty">Chargement…</div>}
        {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}
        {!loading && !error && data && data.items.length === 0 && (
          <div className="devis-empty">
            {tab === 'hardware'
              ? "Aucun produit suivi par num\u00e9ro de s\u00e9rie. C'est le mode de suivi du produit qui le place dans cet onglet : tant qu'une r\u00e9f\u00e9rence est suivie par lot, elle reste dans Consommables."
              : 'Aucun produit.'}
          </div>
        )}
        {!loading && !error && data && data.items.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Code</th><th className="prod">Produit</th>
                <th className="num">Vendu</th>
                <th className="num">Physique</th><th className="num">Réservé</th><th className="num">Libre</th>
                <th className="num">Entrant</th><th className="num">Prév.</th>
                <th className="num">Min</th><th className="num">Max</th>
                <th className="num">{tab === 'hardware' ? 'N° série' : 'Lots'}</th>
                {tab === 'consommables' && <th>Péremption</th>}
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr
                  key={p.id}
                  className={openId === p.id ? 'row-selected' : ''}
                  title="Voir la fiche produit et ses lots"
                  onClick={() => setOpenId(openId === p.id ? null : p.id)}
                >
                  {/* Toute la ligne ouvre la fiche, comme sur Ventes et Achats. Le
                      code garde son allure de lien : c'est lui qui signale que la
                      ligne mène quelque part. */}
                  <td className="mono cell-code">
                    <span className="link-btn">{p.code || `#${p.id}`}</span>
                  </td>
                  <td className="prod cell-title"><span title={p.name}>{stripCode(p.name, p.code)}</span></td>
                  <td className="num" data-label="Vendu">{p.sold ? fmtQty(p.sold) : '—'}</td>
                  <td className={`num ${p.on_hand <= 0 ? 'stock-short' : ''}`} data-label="Physique">{fmtQty(p.on_hand)}</td>
                  <td className="num sm-hide" data-label="Réservé">{p.reserved ? fmtQty(p.reserved) : '—'}</td>
                  <td className={`num ${p.free < 0 ? 'stock-short' : ''}`} data-label="Libre">{fmtQty(p.free)}</td>
                  <td className="num sm-hide" data-label="Entrant">{p.incoming ? fmtQty(p.incoming) : '—'}</td>
                  <td className="num sm-hide" data-label="Prév.">{fmtQty(p.forecast)}</td>
                  <td className="num sm-hide" data-label="Min">{p.min ? fmtQty(p.min) : '—'}</td>
                  <td className="num sm-hide" data-label="Max">{p.max ? fmtQty(p.max) : '—'}</td>
                  <td className="num" data-label={tab === 'hardware' ? 'N° série' : 'Lots'}>
                    {p.lots_count ? p.lots_count : '—'}
                  </td>
                  {/* Péremption la plus proche parmi les lots ENCORE en stock :
                      c'est elle qui décide s'il faut agir sur ce produit. */}
                  {tab === 'consommables' && (
                    <td data-label="Péremption">
                      {fmtDate(p.expiration ?? null)}
                      {p.expired && <span className="badge badge-danger" style={{ marginLeft: 6 }}>périmé</span>}
                      {!p.expired && p.expiring && <span className="badge" style={{ marginLeft: 6 }}>≤ 60 j</span>}
                    </td>
                  )}
                  <td data-label="Statut"><StockStatusBadge status={p.status} /></td>
                </tr>
              )).flatMap((row, i) => {
                const p = data.items[i]!;
                if (openId !== p.id) return [row];
                return [row, (
                  <tr key={`${p.id}-detail`} className="detail-row">
                    <td colSpan={tab === 'consommables' ? 13 : 12}>
                      <ProductPanel
                        detail={detail}
                        detailLoading={detailLoading}
                        detailError={detailError}
                        onClose={() => setOpenId(null)}
                      />
                    </td>
                  </tr>
                )];
              })}
            </tbody>
          </table>
        )}
        {!loading && !error && data && (
          <Pager
            total={data.total} limit={data.limit} offset={data.offset}
            onOffset={(o) => { setOffset(o); setOpenId(null); }}
            unit={['produit', 'produits']}
            capped={data.counts.capped}
            cappedNote="au-delà de 3000 produits stockables, le reste n'est pas lu"
          />
        )}
      </div>

    </div>
  );
}

/**
 * Fiche produit dépliée SOUS la ligne cliquée : la liste fait plusieurs centaines
 * de lignes, un panneau en pied de page serait hors écran.
 */
function ProductPanel({
  detail, detailLoading, detailError, onClose,
}: {
  detail: ProductDetail | null;
  detailLoading: boolean;
  detailError: string | null;
  onClose: () => void;
}) {
  return (
    <>
      {detailLoading && <div className="devis-empty">Chargement de la fiche…</div>}
          {detailError && !detailLoading && <div className="devis-empty err">Erreur : {detailError}</div>}
          {detail && !detailLoading && (() => {
            const d = detail.product;
            return (
              <div style={{ padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0 }}>
                    <span className="mono">{d.code || `#${d.id}`}</span> — {stripCode(d.name, d.code)}
                  </h3>
                  <StockStatusBadge status={d.status} />
                  {!d.active && <span className="badge">archivé</span>}
                  {/* La fiche ne montre que les lots ENCORE en stock : l'historique
                      complet (lots consommés compris) est sur l'écran Traçabilité. */}
                  {d.tracking !== 'none' && (
                    <Link className="link-btn" to={`/lots?q=${encodeURIComponent(d.code || d.name)}`}>
                      Tous les lots →
                    </Link>
                  )}
                  <button className="devis-refresh" style={{ marginLeft: 'auto' }} onClick={onClose}>Fermer</button>
                </div>

                <div className="kpi-grid stock-kpis" style={{ marginTop: 14 }}>
                  <div className="kpi-card"><div className="kpi-label">Physique</div><div className="kpi-value">{fmtQty(d.on_hand)}</div></div>
                  <div className="kpi-card"><div className="kpi-label">Réservé</div><div className="kpi-value">{fmtQty(d.reserved)}</div></div>
                  <div className="kpi-card"><div className="kpi-label">Libre</div><div className="kpi-value">{fmtQty(d.free)}</div></div>
                  <div className="kpi-card"><div className="kpi-label">Entrant</div><div className="kpi-value">{fmtQty(d.incoming)}</div></div>
                  <div className="kpi-card"><div className="kpi-label">Prévisionnel</div><div className="kpi-value">{fmtQty(d.forecast)}</div></div>
                </div>

                <table className="prod-ident" style={{ marginTop: 14 }}>
                  <tbody>
                    <tr><th>Catégorie</th><td>{d.category ?? '—'}</td><th>Unité</th><td>{d.uom ?? '—'}</td></tr>
                    <tr><th>Code-barres</th><td className="mono">{d.barcode ?? '—'}</td><th>Seuils min / max</th><td>{d.min ? fmtQty(d.min) : '—'} / {d.max ? fmtQty(d.max) : '—'}</td></tr>
                    <tr><th>Prix de vente</th><td>{fmtMoney(d.sale_price)}</td><th>Coût</th><td>{d.cost_price ? fmtMoney(d.cost_price) : '—'}</td></tr>
                    <tr>
                      <th>Suivi</th>
                      <td>{d.tracking === 'lot' ? 'par lot' : d.tracking === 'serial' ? 'par n° de série' : 'aucun'}</td>
                      <th>Péremption</th>
                      <td>{d.use_expiration_date ? `oui${d.expiration_time ? ` (${d.expiration_time} j)` : ''}` : 'non gérée'}</td>
                    </tr>
                  </tbody>
                </table>

                <h4 style={{ margin: '18px 0 6px' }}>Lots en stock</h4>
                {d.tracking === 'none' ? (
                  <div style={{ opacity: .7 }}>
                    Produit non suivi par lot : Odoo n'enregistre aucun lot pour cette référence, même si
                    le fournisseur en indique un. Activer le suivi pour la traçabilité de rappel.
                  </div>
                ) : detail.lots.length === 0 ? (
                  <div style={{ opacity: .7 }}>Aucun lot en stock actuellement.</div>
                ) : (
                  <table>
                    <thead><tr><th>Lot</th><th className="num">Quantité</th><th className="num">Réservé</th><th>Péremption</th><th></th></tr></thead>
                    <tbody>
                      {detail.lots.map((l) => (
                        <tr key={l.id}>
                          <td className="mono"><b><LotLink id={l.id} name={l.name} /></b></td>
                          <td className="num">{fmtQty(l.qty)}</td>
                          <td className="num">{l.reserved ? fmtQty(l.reserved) : '—'}</td>
                          <td>
                            {fmtDate(l.expiration)}
                            {l.expired && <span className="badge badge-danger" style={{ marginLeft: 6 }}>périmé</span>}
                          </td>
                          <td><Link className="link-btn" to={`/lots?lot=${l.id}`}>Traçabilité →</Link></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <h4 style={{ margin: '18px 0 6px' }}>Stock par emplacement</h4>
                {detail.stock.length === 0 ? (
                  <div style={{ opacity: .7 }}>Aucun stock.</div>
                ) : (
                  <table>
                    <thead><tr><th>Emplacement</th><th>Lot</th><th className="num">Quantité</th><th className="num">Réservé</th></tr></thead>
                    <tbody>
                      {detail.stock.map((q2, i) => (
                        <tr key={i}>
                          <td>{q2.location}</td>
                          <td className="mono">{q2.lot ? <LotLink name={q2.lot} /> : '—'}</td>
                          <td className="num">{fmtQty(q2.qty)}</td>
                          <td className="num">{q2.reserved ? fmtQty(q2.reserved) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <h4 style={{ margin: '18px 0 6px' }}>Derniers mouvements</h4>
                {detail.movements.length === 0 ? (
                  <div style={{ opacity: .7 }}>Aucun mouvement validé.</div>
                ) : (
                  <table>
                    <thead><tr><th>Date</th><th>Sens</th><th className="num">Qté</th><th>Lot</th><th>Document</th><th>Partenaire</th><th>Origine</th></tr></thead>
                    <tbody>
                      {detail.movements.map((mv, i) => (
                        <tr key={i}>
                          <td>{fmtDate(mv.date)}</td>
                          <td>{SENS[mv.sens] ?? mv.sens}</td>
                          <td className="num">{fmtQty(mv.qty)}</td>
                          <td className="mono">
                            {mv.lot ? <LotLink id={mv.lot_id} name={mv.lot} /> : '—'}
                          </td>
                          <td className="mono"><PickingPdfLink id={mv.picking_id} name={mv.picking} /></td>
                          <td>{mv.partner ?? '—'}</td>
                          <td><OriginLink link={mv.origin_link} label={mv.origin} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })()}
    </>
  );
}
